<script>
    import {countUpPlayers} from '../store';
    import {Toast} from 'svelma';
    import {createEventDispatcher} from "svelte";
    import {HistoryEvent} from "../tactics.model";
    import {HistoryEventType} from "../history-events.enum";
    import {CountUpHistoryEvent} from "../countup.model";

    const dispatch = createEventDispatcher();

    export let rounds = [];

    let scoreInputElement;
    let currentScore = null;
    let currentRound = 0;
    let gameFinished = false;
    let activePlayerIndex = 0;
    let ranking = null;
    let history = [];

    function reset() {
        gameFinished = false;
        activePlayerIndex = 0;
        history = [];
        ranking = null;
        currentRound = 0;
        currentScore = null;

        dispatch('reset', {nrOfRounds: rounds && rounds.length > 0 ? rounds[rounds.length - 1] : 8});
    }

    function newGame() {
        dispatch('newGame');
    }

    function handleEndRound(score) {
        if (gameFinished) return;

        if (score == null || score === '') {
            score = 0;
        }

        const numericScore = parseInt(score, 10);

        if (isNaN(numericScore) || numericScore < 0) {
            Toast.create({message: 'Please enter a valid positive score', type: 'is-danger'});
            return;
        }

        if (numericScore > 180) {
            Toast.create({message: 'More than maximum entered (180)!', type: 'is-danger'});
            return;
        }

        addScore(numericScore);
        nextPlayer();

        if (scoreInputElement) {
            scoreInputElement.focus();
        }
    }

    function addScore(scoreInRound) {
        const previousTotal = $countUpPlayers[activePlayerIndex]?.totalScore || 0;
        const totalAfterRound = calculateNewScore(previousTotal, scoreInRound);
        let activePlayer = $countUpPlayers[activePlayerIndex];
        let roundData = activePlayer.rounds.find(item => item.round === rounds[currentRound]);
        if (roundData) {
            roundData.score = scoreInRound;
        }

        history = [...history, new CountUpHistoryEvent(HistoryEventType.POINTS_ADDED, activePlayerIndex, rounds[currentRound], scoreInRound, previousTotal)];

        activePlayer.rounds = [...activePlayer.rounds];

        countUpPlayers.update(players => {
            players[activePlayerIndex].totalScore = totalAfterRound;
            players[activePlayerIndex].rounds = [...players[activePlayerIndex].rounds];
            return [...players];
        });

        checkWinner();
    }

    function calculateNewScore(oldTotal, score) {
        return oldTotal + score;
    }

    function checkWinner() {
        if (currentRound === rounds.length - 1 && activePlayerIndex === $countUpPlayers.length - 1) {
            ranking = [...$countUpPlayers];
            ranking.sort((player1, player2) => player1.totalScore - player2.totalScore).reverse();
            gameFinished = true;
        }
    }

    function nextPlayer() {
        if (activePlayerIndex === $countUpPlayers.length - 1) {
            activePlayerIndex = 0;
            currentRound = currentRound + 1;
        } else {
            activePlayerIndex = activePlayerIndex + 1;
        }
        currentScore = null;

        history = [...history, new HistoryEvent(HistoryEventType.NEXT_PLAYER, activePlayerIndex, 0)];
    }

    function undo() {
        if (history.length === 0) return;

        gameFinished = false;
        let lastHistoryItem = history[history.length - 1];

        if (!lastHistoryItem) {
            return;
        }

        let player = $countUpPlayers[lastHistoryItem.playerIndex];

        if (lastHistoryItem.type === HistoryEventType.POINTS_ADDED) {
            let segment = lastHistoryItem.segment;
            let roundSegment = player.rounds.find(item => item.round === segment);
            player.totalScore = lastHistoryItem.previousTotal;
            if (roundSegment) {
                roundSegment.score = null;
                roundSegment.totalAfterRound = 0;
            }
        }

        history = history.slice(0, -1);
        countUpPlayers.update(players => {
            if (players[activePlayerIndex]) {
                players[activePlayerIndex].rounds = [...players[activePlayerIndex].rounds];
            }
            return [...players];
        });

        if (lastHistoryItem.type === HistoryEventType.NEXT_PLAYER) {
            if (activePlayerIndex - 1 < 0) {
                if (currentRound > 0) {
                    activePlayerIndex = $countUpPlayers.length - 1;
                    currentRound = currentRound - 1;
                }
            } else {
                activePlayerIndex = activePlayerIndex - 1;
            }

            undo();
        }

        if (scoreInputElement) {
            scoreInputElement.focus();
        }
    }

    function onEnterScore(e) {
        if (e.key === 'Enter' || e.charCode === 13 || e.keyCode === 13) {
            e.preventDefault();
            handleEndRound(currentScore);
        }
    }

    function init(el) {
        el.focus();
    }

    function getRoundScore(player, round) {
        const roundData = player.rounds.find((r) => r.round === round);
        if (!roundData || roundData.score == null) {
            return '';
        }
        return roundData.score;
    }

    function isPlayerActive(player, finished, activeIdx) {
        if (finished) return false;
        return $countUpPlayers[activeIdx]?.name === player?.name;
    }

    function isPlayerWinner(player, finished, rank, playersList) {
        if (!finished) return false;
        if (rank && rank.length > 0) {
            return rank[0].totalScore === player.totalScore && player.totalScore > 0;
        }
        return false;
    }

    function isTurnPlayed(roundIdx, playerIdx, player, round) {
        const roundScore = getRoundScore(player, round);
        if (roundScore !== '') return true;
        if (gameFinished) return true;
        if (roundIdx < currentRound) return true;
        if (roundIdx === currentRound && playerIdx < activePlayerIndex) return true;
        return false;
    }
</script>

<!-- Outer Container -->
<div class="countup-screen w-full h-screen min-h-screen text-slate-900 font-sans antialiased flex flex-col p-3 sm:p-4 md:p-5 lg:p-6 box-border bg-[#f0ece9]">

    <!-- BEGIN: TopControlsBar -->
    <header class="w-full max-w-[1600px] mx-auto flex items-center justify-between gap-3 sm:gap-4 bg-white px-4 sm:px-6 py-2 sm:py-3 rounded-2xl shadow-xs border border-black/5 mb-3 sm:mb-4 shrink-0 flex-wrap md:flex-nowrap">
        <!-- Left Brand & Game Badge -->
        <div class="flex items-center gap-3 sm:gap-4 min-w-[180px]">
            <button
                class="hover:opacity-80 transition-opacity cursor-pointer p-1 -m-1 inline-flex items-center"
                on:click={newGame}
                title="Back to lobby"
                type="button"
            >
                <img alt="Yuma Logo" class="h-9 sm:h-11 w-auto object-contain" src="images/yuma.png"/>
            </button>
            <div class="h-8 w-px bg-slate-200"></div>
        </div>

        <!-- Center Score Input Console -->
        <div class="flex items-center justify-center flex-1 max-w-xl mx-auto order-last md:order-none w-full md:w-auto">
            <div class="flex items-center gap-2 w-full max-w-md bg-stone-100/80 p-1.5 rounded-2xl border border-slate-200 shadow-inner">
                <!-- Numeric Input with placeholder -->
                <div class="relative flex-1">
                    <input
                        bind:this={scoreInputElement}
                        bind:value={currentScore}
                        class="w-full h-11 bg-white text-slate-900 text-lg font-bold pl-4 pr-10 rounded-xl border border-slate-200 placeholder-slate-400 focus:border-[#21e467] focus:ring-2 focus:ring-[#21e467]/30 transition-all text-center tracking-wide outline-none disabled:opacity-50"
                        disabled={gameFinished}
                        max="180"
                        min="0"
                        on:keypress={onEnterScore}
                        placeholder="Score..."
                        type="number"
                        use:init
                    />
                    <span class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400 font-bold select-none pointer-events-none">PTS</span>
                </div>

                <!-- Next Player Button -->
                <button
                    class="h-11 inline-flex items-center justify-center gap-2 bg-[#eba9ff] hover:bg-[#e493fb] active:scale-[0.98] text-[#005d46] font-bold px-5 sm:px-6 rounded-xl transition-all shadow-sm border border-purple-300/30 whitespace-nowrap cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                    disabled={gameFinished}
                    on:click={() => handleEndRound(currentScore)}
                    type="button"
                    title="Next player (shortcut: Enter)"
                >
                    <span>Next player</span>
                    <span class="material-symbols-outlined text-[18px]">arrow_forward</span>
                </button>
            </div>
        </div>

        <!-- Right Quick Actions (Undo, Reset Round, Home) -->
        <div class="flex items-center justify-end gap-2.5 min-w-[180px]">
            <!-- Undo Button -->
            <button
                class="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-pink-50 hover:bg-pink-100 text-rose-600 transition-colors border border-rose-200/50 cursor-pointer active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed"
                disabled={history.length === 0}
                on:click={undo}
                title="Undo last action"
                type="button"
            >
                <span class="material-symbols-outlined text-[20px]">undo</span>
            </button>
            <!-- New Round Button -->
            <button
                class="h-11 inline-flex items-center gap-2 bg-[#6434da] hover:bg-[#5229b5] text-white text-sm font-semibold px-4 rounded-xl transition-colors shadow-sm cursor-pointer active:scale-95"
                on:click={reset}
                title="Reset game"
                type="button"
            >
                <span class="material-symbols-outlined text-[18px]">restart_alt</span>
                <span class="hidden sm:inline">New round</span>
            </button>
            <!-- Exit to Setup / Lobby -->
            <button
                class="h-11 w-11 inline-flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors border border-slate-200 cursor-pointer active:scale-95"
                on:click={newGame}
                title="Exit to Setup"
                type="button"
            >
                <span class="material-symbols-outlined text-[20px]">home</span>
            </button>
        </div>
    </header>
    <!-- END: TopControlsBar -->

    <!-- BEGIN: Main Table Container -->
    <main class="w-full max-w-[1600px] mx-auto flex-1 min-h-0 bg-white rounded-2xl sm:rounded-3xl shadow-sm border border-black/5 flex flex-col overflow-hidden">
        <div class="flex-1 min-h-0 overflow-auto flex flex-col">
            <table class="countup-table w-full h-full flex-1 border-collapse select-none">
                <!-- THEAD: Players Header -->
                <thead>
                    <tr class="compact-row h-12 sm:h-14 text-white text-lg">
                        <!-- Left Header: Game Title -->
                        <th class="w-48 sm:w-60 md:w-72 bg-[#005d46] text-white p-3 sm:p-4 text-left align-middle border-r border-emerald-800/40" style="text-align: left;">
                            <div class="flex items-center justify-start gap-2 sm:gap-3 text-left">
                                <span class="material-symbols-outlined text-[#21e467] text-[24px] sm:text-[28px] shrink-0">calculate</span>
                                <div class="text-left">
                                    <div class="text-base sm:text-lg md:text-xl font-black font-headline tracking-tight text-white leading-tight">
                                        Count up
                                    </div>
                                    <div class="text-[11px] sm:text-xs text-emerald-200/80 font-medium">
                                        Throw highest total
                                    </div>
                                </div>
                            </div>
                        </th>
                        <!-- Dynamic Player Columns (Header) -->
                        {#each $countUpPlayers as player}
                            <th
                                class="px-4 py-2.5 align-middle text-center border-r last:border-r-0 transition-colors {
                                    isPlayerWinner(player, gameFinished, ranking, $countUpPlayers)
                                        ? 'bg-[#21e467] text-[#005d46] border-emerald-600/40 shadow-sm'
                                        : isPlayerActive(player, gameFinished, activePlayerIndex)
                                            ? 'bg-[#fff8e7] text-amber-950 border-amber-200/80 shadow-inner'
                                            : 'bg-[#005d46] text-white border-emerald-900/30'
                                }"
                                style="width: {80 / ($countUpPlayers.length || 1)}%;"
                            >
                                <div class="flex items-center justify-center gap-2 flex-wrap">
                                    {#if isPlayerActive(player, gameFinished, activePlayerIndex) && !isPlayerWinner(player, gameFinished, ranking, $countUpPlayers)}
                                        <span class="inline-block w-2.5 h-2.5 rounded-full bg-[#21e467] border-2 border-emerald-700 animate-pulse"></span>
                                    {/if}
                                    <span class="text-xl sm:text-2xl font-bold font-headline truncate">
                                        {player?.name}
                                    </span>
                                    {#if isPlayerWinner(player, gameFinished, ranking, $countUpPlayers)}
                                        <span class="text-xl sm:text-2xl leading-none select-none inline-flex items-center">
                                            🏆
                                        </span>
                                    {/if}
                                </div>
                            </th>
                        {/each}
                    </tr>

                    <!-- Total Points Row in Header -->
                    <tr class="compact-row h-10 sm:h-11 bg-stone-50 border-b border-slate-200">
                        <th class="w-48 sm:w-60 md:w-72 bg-stone-100/90 text-right pr-6 py-1.5 align-middle font-bold text-slate-500 border-r border-slate-200 text-xs sm:text-sm uppercase tracking-wider font-headline" style="text-align: right;">
                            Total Score
                        </th>
                        {#each $countUpPlayers as player}
                            <th class="px-3 sm:px-4 py-1.5 text-center align-middle border-r last:border-r-0 border-slate-200 bg-stone-50 transition-colors {
                                isPlayerActive(player, gameFinished, activePlayerIndex)
                                    ? 'bg-amber-50/60'
                                    : ''
                            }">
                                <div class="flex items-center justify-center min-h-[26px]">
                                    <span class="text-xl sm:text-2xl md:text-3xl font-black font-headline text-[#005d46] leading-none">
                                        {player.totalScore || 0}
                                    </span>
                                </div>
                            </th>
                        {/each}
                    </tr>
                </thead>

                <!-- TBODY: Rounds & Scores -->
                <tbody class="divide-y divide-slate-200">
                    {#each rounds as round, roundIdx}
                        <tr class="round-row transition-colors {roundIdx % 2 === 1 ? 'bg-stone-50/50' : 'bg-white'} {roundIdx === currentRound && !gameFinished ? 'bg-[#fef0cd]/30' : ''}">
                            <!-- Left: Round Number -->
                            <td class="round-label text-left pl-6 sm:pl-8 py-1 sm:py-2 align-middle border-r border-slate-200">
                                <div class="flex items-center gap-3">
                                    {#if roundIdx === currentRound && !gameFinished}
                                        <span class="w-2.5 h-2.5 rounded-full bg-[#21e467] border border-emerald-600 shrink-0"></span>
                                    {/if}
                                    <span class="text-2xl sm:text-3xl md:text-4xl font-black tracking-tight leading-none font-headline {roundIdx === currentRound && !gameFinished ? 'text-emerald-900' : 'text-slate-600'}">
                                        Round {round}
                                    </span>
                                </div>
                            </td>

                            <!-- Dynamic Player Round Cells -->
                            {#each $countUpPlayers as player, playerIdx}
                                <td class="px-3 sm:px-4 py-1 sm:py-2 text-center align-middle border-r last:border-r-0 border-slate-200 transition-colors {
                                    isPlayerActive(player, gameFinished, activePlayerIndex) && roundIdx === currentRound
                                        ? 'bg-[#fef0cd]/70 border-r border-amber-300/80'
                                        : ''
                                }">
                                    <div class="flex items-center justify-center w-full min-h-[32px] sm:min-h-[38px]">
                                        {#if isPlayerActive(player, gameFinished, activePlayerIndex) && roundIdx === currentRound}
                                            <span class="inline-flex items-center justify-center px-3 py-1 rounded-lg border-2 border-dashed border-amber-400/80 bg-amber-100/60 text-amber-800 text-sm sm:text-base font-bold animate-pulse leading-none shadow-sm">
                                                Throwing...
                                            </span>
                                        {:else if isTurnPlayed(roundIdx, playerIdx, player, round)}
                                            <span class="text-xl sm:text-2xl md:text-3xl font-black font-headline text-slate-800 leading-none">
                                                {getRoundScore(player, round) !== '' ? getRoundScore(player, round) : '0'}
                                            </span>
                                        {:else}
                                            <span class="text-slate-300 font-bold text-lg select-none leading-none">-</span>
                                        {/if}
                                    </div>
                                </td>
                            {/each}
                        </tr>
                    {/each}
                </tbody>
            </table>
        </div>

        <!-- Footer Info Strip -->
        <footer class="h-10 sm:h-11 bg-stone-50 border-t border-slate-200 px-6 flex items-center justify-between text-xs sm:text-sm text-slate-500 font-medium shrink-0">
            <div class="flex items-center gap-2">
                <span class="material-symbols-outlined text-emerald-700 text-base">info</span>
                <span>Enter 3-dart score (0-180) each round. Highest total score wins the match.</span>
            </div>
            <div class="flex items-center gap-4 hidden sm:flex">
                <span>Press <kbd class="px-1.5 py-0.5 bg-white rounded border border-slate-300 font-mono text-xs font-semibold text-slate-700 shadow-2xs">Enter</kbd> to submit score</span>
            </div>
        </footer>
    </main>
    <!-- END: Main Table Container -->
</div>

<style>
    :global(body) {
        margin: 0;
        padding: 0;
        background-color: #f0ece9;
    }

    .countup-screen {
        font-family: 'Space Grotesk', 'Inter', sans-serif;
    }

    .countup-screen table,
    .countup-table {
        table-layout: fixed;
        width: 100%;
        height: 100%;
    }

    .countup-screen table th,
    .countup-screen table td,
    .countup-table th,
    .countup-table td {
        vertical-align: middle !important;
    }

    .countup-screen .compact-row,
    .compact-row {
        height: 1px;
    }

    .countup-screen .round-row,
    .round-row {
        height: 1%;
    }
</style>
